import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Sparkles, Send, Loader2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { userReportsApi } from "../../api";
import type { DashboardChatResult } from "../../api";
import { useBrianProposal } from "../../hooks";
import BrianPromptBar from "../Brian/BrianPromptBar";
import BrianProposalCard from "../Brian/BrianProposalCard";

const CHART_TYPE_LABELS: Record<string, string> = {
  bar: "Bar chart",
  line: "Line chart",
  number: "Number",
  table: "Table",
};

interface DashboardChatProps {
  dashboardId: string;
  onWidgetAdded?: () => void;
  open: boolean;
  onClose: () => void;
}

export default function DashboardChat({ dashboardId, onWidgetAdded, open, onClose }: DashboardChatProps) {
  const [input, setInput] = useState("");
  const [clarifyInput, setClarifyInput] = useState("");
  const [addingWidget, setAddingWidget] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { response, loading, error, send, dismiss: dismissProposal, reset } =
    useBrianProposal<DashboardChatResult>({
      request: async (message, conversation) =>
        (await userReportsApi.dashboardChat(dashboardId, message, conversation)).data,
      errorMessage: "Brian could not create that widget",
    });

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    void send(msg);
  }

  function handleClarifySubmit(e: React.FormEvent) {
    e.preventDefault();
    const msg = clarifyInput.trim();
    if (!msg || loading) return;
    setClarifyInput("");
    void send(msg);
  }

  function dismiss() {
    dismissProposal();
    setClarifyInput("");
    inputRef.current?.focus();
  }

  async function handleAddWidget() {
    if (!response?.widget) return;
    setAddingWidget(true);
    try {
      const w = response.widget;
      await userReportsApi.create({
        name: w.name,
        description: w.description || null,
        sql_query: w.sql_query,
        dashboard_id: dashboardId,
        pinned_to_dashboard: true,
        dashboard_config: {
          chart_type: w.chart_type || "table",
          x_col: w.x_col || "",
          y_col: w.y_col || "",
        },
      });
      reset();
      setClarifyInput("");
      onWidgetAdded?.();
    } catch { return; } finally {
      setAddingWidget(false);
    }
  }

  return (
    <div className="px-4 md:px-6 pt-4 pb-2">
      {/* Input bar */}
      <BrianPromptBar
        inputRef={inputRef}
        autoFocus
        value={input}
        onChange={setInput}
        onSubmit={handleSubmit}
        loading={loading}
        placeholder="Ask Brian to add a widget or explain your data…"
        onClose={onClose}
      />

      {error && <div className="mt-3"><NotFoundCard message={error} onDismiss={dismiss} /></div>}

      {/* Response card */}
      {response && (
        <div className="mt-3">
          {response.type === "clarification" && (
            <ClarificationCard
              message={response.message}
              clarifyInput={clarifyInput}
              setClarifyInput={setClarifyInput}
              onSubmit={handleClarifySubmit}
              loading={loading}
              onDismiss={dismiss}
            />
          )}

          {response.type === "not_found" && (
            <NotFoundCard message={response.message} onDismiss={dismiss} />
          )}

          {response.type === "widget_proposal" && (
            <WidgetProposalCard
              response={response}
              onAdd={handleAddWidget}
              onDismiss={dismiss}
              adding={addingWidget}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface ClarificationCardProps {
  message: string;
  clarifyInput: string;
  setClarifyInput: Dispatch<SetStateAction<string>>;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  onDismiss: () => void;
}

function ClarificationCard({ message, clarifyInput, setClarifyInput, onSubmit, loading, onDismiss }: ClarificationCardProps) {
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5">
          <Sparkles size={15} className="mt-0.5 shrink-0 text-[#1D345F]" />
          <p className="text-sm text-gray-800">{message}</p>
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
          title="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
      <form onSubmit={onSubmit} className="mt-3 flex gap-2">
        <input
          type="text"
          value={clarifyInput}
          onChange={(e) => setClarifyInput(e.target.value)}
          disabled={loading}
          placeholder="Your answer…"
          className="flex-1 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:border-[#1D345F] focus:outline-none focus:ring-1 focus:ring-[#1D345F] disabled:opacity-60"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading || !clarifyInput.trim()}
          className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Send
        </button>
      </form>
    </div>
  );
}

function NotFoundCard({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-500" />
          <p className="text-sm text-gray-700">{message}</p>
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
          title="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

interface WidgetProposalCardProps {
  response: DashboardChatResult;
  onAdd: () => void;
  onDismiss: () => void;
  adding: boolean;
}

function WidgetProposalCard({ response, onAdd, onDismiss, adding }: WidgetProposalCardProps) {
  const w = response.widget;
  const preview = response.preview;

  return (
    <BrianProposalCard
      message={response.message}
      onDismiss={onDismiss}
      actions={
        <>
          <button
            onClick={onDismiss}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-gray-800"
          >
            Dismiss
          </button>
          <button
            onClick={onAdd}
            disabled={adding}
            className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#162847] disabled:opacity-50"
          >
            {adding ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Add to dashboard
          </button>
        </>
      }
    >
      {w && (
        <div>
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">{w.name}</p>
              {w.description && (
                <p className="mt-0.5 text-xs text-gray-500">{w.description}</p>
              )}
            </div>
            {w.chart_type && (
              <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {CHART_TYPE_LABELS[w.chart_type] ?? w.chart_type}
              </span>
            )}
          </div>

          {/* Preview table */}
          {preview?.error ? (
            <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              Preview error: {preview.error}
            </div>
          ) : (preview?.rows?.length ?? 0) > 0 ? (
            <PreviewTable rows={preview!.rows} />
          ) : (
            <p className="text-xs text-gray-400">No preview data available.</p>
          )}
        </div>
      )}
    </BrianProposalCard>
  );
}

function PreviewTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows || rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  const displayRows = rows.slice(0, 5);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-100">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50">
            {cols.map((col) => (
              <th
                key={col}
                className="whitespace-nowrap px-2.5 py-1.5 text-left font-medium text-gray-500"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, i) => (
            <tr key={i} className={i % 2 === 1 ? "bg-gray-50/50" : ""}>
              {cols.map((col) => (
                <td
                  key={col}
                  className="whitespace-nowrap px-2.5 py-1.5 text-gray-700"
                >
                  {row[col] == null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    String(row[col])
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
